import type { Cents } from "./types.js";

export type AccountSaleSettlementQuote = {
  outstandingCents: Cents;
  configuredDiscountCents: Cents;
  expectedReceivedCents: Cents;
};

export type AccountPaymentLineQuote = {
  receivedCents: Cents;
  discountPct: number;
  discountCents: Cents;
  debtCancelledCents: Cents;
};

function assertDiscountPct(discountPct: number) {
  if (!Number.isFinite(discountPct) || discountPct < 0 || discountPct >= 100) {
    throw new RangeError("discountPct must be between 0 (inclusive) and 100 (exclusive)");
  }
}

// Converts a real amount received into the debt it pays off. Rounding happens
// once, in cents, and the discount is the exact remainder, so the invariant
// always holds even when the percentage cannot be represented exactly.
export function quotePaymentLineFromReceived(input: {
  receivedCents: Cents;
  discountPct: number;
}): AccountPaymentLineQuote {
  if (!Number.isInteger(input.receivedCents) || input.receivedCents <= 0) {
    throw new RangeError("receivedCents must be a positive integer");
  }
  assertDiscountPct(input.discountPct);
  const debtCancelledCents = Math.round(input.receivedCents / (1 - input.discountPct / 100));
  return {
    receivedCents: input.receivedCents,
    discountPct: input.discountPct,
    discountCents: debtCancelledCents - input.receivedCents,
    debtCancelledCents,
  };
}

// Converts a target debt reduction into the amount to collect. This is useful
// when an operator selects a sale (or a portion of it) first.
export function quotePaymentLineFromDebt(input: {
  debtCancelledCents: Cents;
  discountPct: number;
}): AccountPaymentLineQuote {
  if (!Number.isInteger(input.debtCancelledCents) || input.debtCancelledCents <= 0) {
    throw new RangeError("debtCancelledCents must be a positive integer");
  }
  assertDiscountPct(input.discountPct);
  const receivedCents = Math.round(input.debtCancelledCents * (1 - input.discountPct / 100));
  return {
    receivedCents,
    discountPct: input.discountPct,
    discountCents: input.debtCancelledCents - receivedCents,
    debtCancelledCents: input.debtCancelledCents,
  };
}

// Quotes the automatic final price for paying one pending sale with a payment
// method that grants a discount. The result is still a quote: an operator can
// agree to a different final amount, but that difference becomes an explicit
// manual discount with a mandatory reason at the API boundary.
export function quoteAccountSaleSettlement(input: {
  outstandingCents: Cents;
  discountPct: number;
}): AccountSaleSettlementQuote {
  if (!Number.isInteger(input.outstandingCents) || input.outstandingCents <= 0) {
    throw new RangeError("outstandingCents must be a positive integer");
  }
  assertDiscountPct(input.discountPct);
  const configuredDiscountCents = Math.round((input.outstandingCents * input.discountPct) / 100);
  return {
    outstandingCents: input.outstandingCents,
    configuredDiscountCents,
    expectedReceivedCents: input.outstandingCents - configuredDiscountCents,
  };
}

export type AccountSaleAllocationEvaluation = {
  cashAppliedCents: Cents;
  discountCents: Cents;
  appliedCents: Cents;
  remainingCents: Cents;
  isSettled: boolean;
  requiresManualDiscountReason: boolean;
};

// Validates the economic side of a single-sale allocation. It does not record
// money and it never changes the sale amount: cash + discount explains how
// much of the already-existing debt was cancelled.
export function evaluateAccountSaleAllocation(input: {
  outstandingCents: Cents;
  cashAppliedCents: Cents;
  discountCents: Cents;
  configuredDiscountCents: Cents;
}): AccountSaleAllocationEvaluation {
  for (const [name, amount] of Object.entries(input)) {
    if (!Number.isInteger(amount) || amount < 0) {
      throw new RangeError(`${name} must be a non-negative integer`);
    }
  }
  if (input.outstandingCents === 0) {
    throw new RangeError("cannot allocate against a settled sale");
  }
  const appliedCents = input.cashAppliedCents + input.discountCents;
  if (appliedCents === 0) {
    throw new RangeError("an allocation must apply cash or discount");
  }
  if (appliedCents > input.outstandingCents) {
    throw new RangeError("allocation exceeds outstanding debt");
  }
  return {
    cashAppliedCents: input.cashAppliedCents,
    discountCents: input.discountCents,
    appliedCents,
    remainingCents: input.outstandingCents - appliedCents,
    isSettled: appliedCents === input.outstandingCents,
    requiresManualDiscountReason: input.discountCents !== input.configuredDiscountCents,
  };
}
